import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import MainMenu from "./MainMenu";
import Chatbot from "./Chatbot";

type ParentProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onMobileClose?: () => void;
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
};

const SideNav: React.FC<ParentProps> = ({ isOpen, setIsOpen, onMobileClose, isCollapsed: externalCollapsed, onCollapseChange }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false); // Start with main menu expanded
  const [activePanel, setActivePanel] = useState<"menu" | "chat" | null>(isOpen ? "menu" : null);
  const isMobile = useMediaQuery("(max-width:772px)");
  const prevIsOpenRef = useRef<boolean | undefined>(undefined);

  // Use external collapsed state if provided, otherwise use internal state
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  // Sync activePanel with isOpen prop changes
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;

    if (!isOpen) {
      setActivePanel(null);
    } else if (isOpen && (prevIsOpen === undefined || !prevIsOpen)) {
      setActivePanel("menu");
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, isMobile]);

  // Handle panel toggle
  const handlePanelToggle = (panel: "menu" | "chat") => {
    if (isMobile) {
      setIsOpen(true);
      setActivePanel(activePanel === panel ? null : panel);
    } else {
      if (activePanel === panel) {
        setActivePanel(null);
        setIsOpen(false);
      } else {
        setActivePanel(panel);
        setIsOpen(true);
      }
    }
  };

  const handleChatbotClose = () => {
    setActivePanel(null);
    setIsOpen(false);
  };

  const handleCollapseChange = (collapsed: boolean) => {
    if (externalCollapsed === undefined) {
      setInternalCollapsed(collapsed);
    }
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  };

  // Mobile View
  if (isMobile) {
    return (
      <>
        {/* Icon rail is always visible on small screens (non-overlay) */}
        <Box
          sx={{
            backgroundColor: 'black',
            color: 'white',
            minHeight: '100vh',
            width: '80px',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRight: '1px solid #333',
            position: 'relative',
            flexShrink: 0,
          }}
          className="side-nav-scrollable"
        >
          <MainMenu
            onMobileClose={onMobileClose}
            isCollapsed={true}
            onCollapseChange={handleCollapseChange}
          />
        </Box>

        {isOpen && !isCollapsed && (
          <>
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                backgroundColor: 'black',
                color: 'white',
                width: '250px',
                zIndex: 1001,
                overflowY: 'auto',
                borderRight: '1px solid #333',
                boxShadow: 24,
                transition: 'width 0.3s ease',
              }}
              className="side-nav-scrollable"
            >
              <MainMenu
                onMobileClose={onMobileClose}
                isCollapsed={isCollapsed}
                onCollapseChange={handleCollapseChange}
              />
            </Box>
            {/* Overlay backdrop */}
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: '250px',
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.48)',
                zIndex: 999,
              }}
              onClick={() => {
                setIsOpen(true);
                handleCollapseChange(true);
                if (onMobileClose) onMobileClose();
              }}
            />
          </>
        )}

        {/* Chatbot Panel - Overlay on mobile */}
        {activePanel === "chat" && isOpen && (
          <>
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: isCollapsed ? '80px' : '250px',
                bottom: 0,
                backgroundColor: 'black',
                color: 'white',
                width: '350px',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid #333',
                boxShadow: 24,
              }}
            >
              <Chatbot
                onClose={handleChatbotClose}
                onMobileClose={onMobileClose}
              />
            </Box>
            {/* Overlay backdrop */}
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: isCollapsed ? '430px' : '430px',
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.48)',
                zIndex: 999,
              }}
              onClick={() => {
                setIsOpen(false);
                if (onMobileClose) onMobileClose();
              }}
            />
          </>
        )}
      </>
    );
  }

  // Desktop View
  return (
    <>
      {/* Main Menu - Always show, but collapsed or expanded */}
      {isOpen && (
        <Box
          sx={{
            backgroundColor: 'black',
            color: 'white',
            minHeight: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRight: '1px solid',
            borderColor: 'grey.700',
            width: isCollapsed ? '80px' : '220px',
            transition: 'width 0.3s ease',
            position: 'relative',
            flexShrink: 0,
          }}
          className="side-nav-scrollable"
        >
              <MainMenu
                isCollapsed={isCollapsed}
                onCollapseChange={handleCollapseChange}
              />
            </Box>
          )}

      {/* Chatbot Panel */}
      {activePanel === "chat" && isOpen && (
        <Box
          sx={{
            backgroundColor: 'black',
            color: 'white',
            minHeight: '100vh',
            width: '350px',
            borderRight: '1px solid #333',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Chatbot onClose={handleChatbotClose} />
        </Box>
      )}
    </>
  );
};

export default SideNav;
